import React from "react";
import { CreditCard, Loader2, ShieldCheck, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Conferma esplicita prima dell'addebito 1-click off-session.
 * Mostra prezzo, prodotto e card on file. Conforme art. 51 Codice del Consumo (IT).
 */
export default function UpsellConfirmDialog({ open, onClose, onConfirm, loading, error, productName, priceEur, lastCardDigits }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onClose && onClose(); }}>
      <DialogContent data-testid="upsell-confirm-dialog" className="sm:max-w-[460px] rounded-2xl border-0 p-0 overflow-hidden bg-white">
        <div className="h-2 bg-tension w-full" />
        <div className="px-7 pt-6 pb-7">
          <DialogHeader className="space-y-2 text-left">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-1" style={{ background: "var(--np-quaternary)" }}>
              <CreditCard size={20} style={{ color: "var(--np-secondary)" }} />
            </div>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
              Conferma acquisto
            </DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
              Stai per acquistare con un click usando la carta già salvata. Verifica il riepilogo qui sotto prima di confermare.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-3">
            <div className="rounded-xl border-2 p-4 flex items-center justify-between gap-3" style={{ borderColor: "var(--np-secondary)", background: "#fff8f4" }}>
              <div>
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--np-muted)" }}>Prodotto</p>
                <p className="text-base font-bold mt-0.5" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>{productName}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--np-muted)" }}>Totale</p>
                <p className="text-2xl font-bold mt-0.5" style={{ color: "var(--np-secondary)", fontFamily: "Merriweather, serif" }} data-testid="upsell-confirm-price">
                  € {priceEur.toFixed(2).replace(".", ",")}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-[#e3eaf2] bg-[#f7fafd] p-3 text-[13px] flex items-start gap-2" style={{ color: "var(--np-muted)" }}>
              <ShieldCheck size={14} className="mt-0.5 shrink-0" style={{ color: "var(--np-secondary)" }} />
              <div>
                <p style={{ color: "var(--np-primary)" }} className="font-medium">Pagamento sicuro 1-click</p>
                <p className="mt-0.5">
                  Verrà addebitato l&apos;importo sulla carta usata nell&apos;acquisto precedente
                  {lastCardDigits ? <> (•••• {lastCardDigits})</> : null}. Nessun altro costo o rinnovo automatico.
                </p>
              </div>
            </div>

            <p className="text-[11px]" style={{ color: "var(--np-muted)" }}>
              Cliccando &quot;Conferma e paga&quot; autorizzi l&apos;addebito di € {priceEur.toFixed(2).replace(".", ",")} sulla carta salvata, ai sensi
              dell&apos;art. 51 Codice del Consumo. Per acquisti digitali ad accesso immediato non si applica il diritto di
              recesso (art. 59 lett. o).
            </p>

            {error && <p data-testid="upsell-confirm-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

            <div className="flex flex-wrap gap-3 pt-2">
              <button type="button" onClick={onClose} disabled={loading} data-testid="upsell-confirm-cancel"
                className="px-4 py-2.5 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
                <X size={14} /> Annulla
              </button>
              <button type="button" onClick={onConfirm} disabled={loading} data-testid="upsell-confirm-pay"
                className="cta-btn flex-1 disabled:opacity-60">
                {loading ? <><Loader2 className="animate-spin" size={16} /> Pagamento in corso…</> : <>Conferma e paga € {priceEur.toFixed(2).replace(".", ",")}</>}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
